import { useCallback, useEffect, useMemo, useState } from 'react';

import type {
  AddableContentType,
  AddableQuestionType,
  AddableQuestionPartType,
  EditableGradingRubric,
  EditableContentUnit,
  EditableQuestion,
  EditableQuestionPart,
  EditableVariable,
  EditorState,
  QuestionSectionKind,
  RawComponent,
  RawQuestionSettings,
  RawSection,
} from './editorTypes';
import { createEditorState } from './editorNormalizer';
import {
  createDraftGradingRubric,
  getRubricTotalScore,
  normalizeGradingRubric,
} from './questionDialogUtils';
import {
  remapAssignedIndicesAfterDelete,
  reorderAssignment,
} from './questionAssignmentUtils';
import { normalizeQuestionForEditor } from './questionNormalizerEditor';

export interface AddVideoResult {
  error?: string;
  success: boolean;
}

function createGeneratedId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }

  return `editor-unit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeString(value: string | undefined) {
  const normalized = value?.trim();
  return normalized ? normalized : undefined;
}

function hasVariableBasedAnswer(
  value: string | undefined,
) {
  return Boolean(normalizeString(value));
}

function createRubricValidationSnapshot(
  rubric: EditableGradingRubric | undefined,
) {
  if (!rubric) {
    return undefined;
  }

  const normalizedRubric = normalizeGradingRubric(rubric);

  return {
    rubricUnits: normalizedRubric.rubricUnits.map((unit) => ({
      details: normalizeString(unit.details) ?? '',
      name: normalizeString(unit.name) ?? '',
      score: unit.score,
    })),
    totalScore: getRubricTotalScore(normalizedRubric),
  };
}

function createPartValidationSnapshot(part: EditableQuestionPart) {
  return {
    answerUnits: normalizeString(part.answerUnits),
    contentHtml: part.contentHtml ?? '<p></p>',
    correctAnswerIndex: part.correctAnswerIndex,
    detailedAnswerHtml: part.detailedAnswerHtml ?? '',
    exactAnswer: normalizeString(part.exactAnswer),
    gradingRubric: createRubricValidationSnapshot(part.gradingRubric),
    hintHtml: part.hintHtml ?? '',
    maxNumAttempts: part.maxNumAttempts,
    numericalAnswer: part.numericalAnswer,
    numericalError: part.numericalError,
    options: [...part.options],
    type: part.type,
    useCustomNumAttempts: part.useCustomNumAttempts ?? false,
    value: part.value,
    variableBasedAnswer: normalizeString(part.variableBasedAnswer),
  };
}

function createQuestionValidationSnapshot(question: EditableQuestion) {
  return {
    answerUnits: normalizeString(question.answerUnits),
    contentHtml: question.contentHtml ?? '<p></p>',
    correctAnswerIndex: question.correctAnswerIndex,
    creditForParticipation: question.creditForParticipation ?? false,
    detailedAnswerHtml: question.detailedAnswerHtml ?? '',
    exactAnswer: normalizeString(question.exactAnswer),
    gradingRubric: createRubricValidationSnapshot(question.gradingRubric),
    hintHtml: question.hintHtml ?? '',
    imagePaths: [...(question.imagePaths ?? [])],
    maxNumAttempts: question.maxNumAttempts,
    numericalAnswer: question.numericalAnswer,
    numericalError: question.numericalError,
    options: [...question.options],
    parts: (question.parts ?? []).map((part) => createPartValidationSnapshot(part)),
    type: question.type,
    useCustomNumAttempts: question.useCustomNumAttempts ?? false,
    useRandomVariables: question.useRandomVariables ?? false,
    value: question.value,
    variableBasedAnswer: normalizeString(question.variableBasedAnswer),
    variables: question.variables.map((variable) => ({
      maxValue: variable.maxValue,
      minValue: variable.minValue,
      name: variable.name,
      type: variable.type,
    })),
  };
}

function isQuestionUnchanged(question: EditableQuestion) {
  if (question.isNew || !question.rawOriginal) {
    return false;
  }

  const originalQuestion = normalizeQuestionForEditor(question.rawOriginal, 0);

  return (
    JSON.stringify(createQuestionValidationSnapshot(question)) ===
    JSON.stringify(createQuestionValidationSnapshot(originalQuestion))
  );
}

function clearQuestionValidation(question: EditableQuestion): EditableQuestion {
  return {
    ...question,
    parts: question.parts?.map((part) => ({
      ...part,
      validationError: undefined,
    })),
    validationError: undefined,
  };
}

function findDefaultOwnership(baseComponent: RawComponent) {
  let fallbackOwnership: {
    sourceContentSectionIndex: number;
    sourceContentSectionId?: string;
    sourceSectionIndex: number;
    sourceSectionId: string;
  } | null = null;

  for (const [sectionIndex, section] of (baseComponent.sections ?? []).entries()) {
    const contentSection = section.content_section ?? section.contentSection;
    const sourceSectionId =
      typeof section.id === 'number'
        ? String(section.id)
        : typeof section.uid === 'number'
          ? String(section.uid)
          : section.id?.toString().trim() ||
            section.uid?.toString().trim() ||
            `__generated-section-${sectionIndex + 1}`;

    if (!fallbackOwnership) {
      fallbackOwnership = {
        sourceContentSectionIndex: sectionIndex,
        sourceContentSectionId: undefined,
        sourceSectionIndex: sectionIndex,
        sourceSectionId,
      };
    }

    if (!contentSection) {
      continue;
    }

    const sourceContentSectionId =
      typeof contentSection.id === 'number'
        ? String(contentSection.id)
        : typeof contentSection.uid === 'number'
          ? String(contentSection.uid)
          : contentSection.id?.toString().trim() ||
            contentSection.uid?.toString().trim() ||
            `__generated-content-section-${sectionIndex + 1}`;

    return {
      sourceContentSectionIndex: sectionIndex,
      sourceContentSectionId,
      sourceSectionIndex: sectionIndex,
        sourceSectionId,
    };
  }

  return fallbackOwnership;
}

function getSectionIdentifier(section: RawSection, sectionIndex: number) {
  if (typeof section.id === 'number') {
    return String(section.id);
  }

  if (typeof section.uid === 'number') {
    return String(section.uid);
  }

  return (
    section.id?.toString().trim() ||
    section.uid?.toString().trim() ||
    `__generated-section-${sectionIndex + 1}`
  );
}

function createEditableOwnerSection(baseComponent: RawComponent) {
  const sectionId = `editor-section-${createGeneratedId()}`;
  const nextSection: RawSection = {
    content_section: {
      content_units: [],
      id: `${sectionId}-content`,
    },
    id: sectionId,
  };
  const nextSections = [...(baseComponent.sections ?? []), nextSection];
  const sourceSectionIndex = nextSections.length - 1;

  return {
    baseComponent: {
      ...baseComponent,
      sections: nextSections,
    },
    ownership: {
      sourceContentSectionIndex: sourceSectionIndex,
      sourceContentSectionId: `${sectionId}-content`,
      sourceSectionIndex,
      sourceSectionId: sectionId,
    },
  };
}

function ensureEditableOwnerSection(state: EditorState) {
  const ownership = findDefaultOwnership(state.baseComponent);

  if (ownership) {
    return {
      ownership,
      state,
    };
  }

  const { baseComponent, ownership: nextOwnership } =
    createEditableOwnerSection(state.baseComponent);

  return {
    ownership: nextOwnership,
    state: {
      ...state,
      baseComponent,
    },
  };
}

function ensureOwnerSectionId(state: EditorState) {
  const prepared = ensureEditableOwnerSection(state);

  return {
    ownerSectionId: prepared.ownership.sourceSectionId,
    state: prepared.state,
  };
}

function ensureVideoOwnerSection(state: EditorState) {
  if (state.videoSectionOwnerSectionId) {
    return state;
  }

  const { ownerSectionId, state: preparedState } = ensureOwnerSectionId(state);

  return {
    ...preparedState,
    videoSectionOwnerSectionId: ownerSectionId,
  };
}

function ensureQuestionOwnerSection(
  state: EditorState,
  kind: QuestionSectionKind,
) {
  const ownerKey =
    kind === 'preLecture'
      ? 'preLectureQuestionSectionOwnerSectionId'
      : 'questionSectionOwnerSectionId';

  if (state[ownerKey]) {
    return state;
  }

  const { ownerSectionId, state: preparedState } = ensureOwnerSectionId(state);

  return {
    ...preparedState,
    [ownerKey]: ownerSectionId,
  };
}

function getFallbackVideoSectionOwner(baseComponent: RawComponent) {
  const [firstSection] = baseComponent.sections ?? [];

  if (!firstSection) {
    return undefined;
  }

  return getSectionIdentifier(firstSection, 0);
}

function withFallbackVideoSectionOwner(state: EditorState | null) {
  if (!state?.baseComponent || state.videoSectionOwnerSectionId) {
    return state;
  }

  const fallbackOwner = getFallbackVideoSectionOwner(state.baseComponent);

  return fallbackOwner
    ? {
        ...state,
        videoSectionOwnerSectionId: fallbackOwner,
      }
    : state;
}

function getOwnershipForInsertion(
  baseComponent: RawComponent,
  contentUnits: EditableContentUnit[],
  insertIndex: number,
) {
  const previousUnit = contentUnits[insertIndex - 1];

  if (previousUnit) {
    return {
      sourceContentSectionIndex: previousUnit.sourceContentSectionIndex,
      sourceContentSectionId: previousUnit.sourceContentSectionId,
      sourceSectionIndex: previousUnit.sourceSectionIndex,
      sourceSectionId: previousUnit.sourceSectionId,
    };
  }

  const nextUnit = contentUnits[insertIndex];

  if (nextUnit) {
    return {
      sourceContentSectionIndex: nextUnit.sourceContentSectionIndex,
      sourceContentSectionId: nextUnit.sourceContentSectionId,
      sourceSectionIndex: nextUnit.sourceSectionIndex,
      sourceSectionId: nextUnit.sourceSectionId,
    };
  }

  return findDefaultOwnership(baseComponent);
}

function createEmptyUnit(
  editorType: AddableContentType,
  ownership: {
    sourceContentSectionIndex: number;
    sourceContentSectionId?: string;
    sourceSectionIndex: number;
    sourceSectionId: string;
  },
): EditableContentUnit {
  const baseUnit: EditableContentUnit = {
    caption: undefined,
    collapseByDefault: false,
    disableAutoNumbering: false,
    editorType,
    heading: undefined,
    id: createGeneratedId(),
    isNew: true,
    sourceContentSectionIndex: ownership.sourceContentSectionIndex,
    sourceContentSectionId: ownership.sourceContentSectionId,
    sourceSectionIndex: ownership.sourceSectionIndex,
    sourceSectionId: ownership.sourceSectionId,
  };

  if (editorType === 'IMAGE') {
    return {
      ...baseUnit,
      imageSize: 'MEDIUM',
    };
  }

  if (editorType === 'VIDEO') {
    return {
      ...baseUnit,
      videoId: '',
    };
  }

  if (editorType === 'IFRAME') {
    return {
      ...baseUnit,
      iframeHeight: 420,
      iframeSource: '',
      iframeWidth: 100,
    };
  }

  if (editorType === 'EQUATION') {
    return {
      ...baseUnit,
      latex: '',
      originalText: undefined,
    };
  }

  if (editorType === 'TABLE') {
    return {
      ...baseUnit,
      tableDensity: 'comfortable',
      // On by default: instructors do not think to set it, and it decides both
      // the look and the screen reader semantics.
      tableHeaderRow: true,
      tableHtml: '',
      tableStickyHeader: false,
      tableStyle: 'plain',
    };
  }

  return {
    ...baseUnit,
    originalText: undefined,
    text: '<p></p>',
  };
}

function moveArrayItem<T>(items: T[], fromIndex: number, toIndex: number) {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);

  if (movedItem === undefined) {
    return nextItems;
  }

  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
}

function hasImageUrl(unit: EditableContentUnit) {
  return typeof unit.imageUrl === 'string' && unit.imageUrl.trim().length > 0;
}

function getUnitValidationError(unit: EditableContentUnit) {
  if (unit.editorType === 'IMAGE' && !hasImageUrl(unit)) {
    return 'An image is required. Upload an image or delete this block.';
  }

  return undefined;
}

function createEmptyQuestionPart(
  type: AddableQuestionPartType,
): EditableQuestionPart {
  const gradingRubric =
    type === 'AI_UPLOAD' ? createDraftGradingRubric() : undefined;

  return {
    contentHtml: '<p></p>',
    correctAnswerIndex: type === 'MULTIPLE_CHOICE' ? 0 : undefined,
    gradingRubric,
    id: createGeneratedId(),
    maxNumAttempts: type === 'AI_UPLOAD' ? 1 : undefined,
    options: type === 'MULTIPLE_CHOICE' ? ['', ''] : [],
    type,
    useCustomNumAttempts: type === 'AI_UPLOAD' ? true : undefined,
    value: type === 'AI_UPLOAD' ? getRubricTotalScore(gradingRubric) : 1,
    variableBasedAnswer: type === 'AI_UPLOAD' ? undefined : '',
  };
}

function createEmptyQuestion(type: AddableQuestionType): EditableQuestion {
  const gradingRubric =
    type === 'AI_UPLOAD' ? createDraftGradingRubric() : undefined;

  return {
    contentHtml: '<p></p>',
    correctAnswerIndex: type === 'MULTIPLE_CHOICE' ? 0 : undefined,
    creditForParticipation: false,
    gradingRubric,
    id: createGeneratedId(),
    imagePaths: [],
    isNew: true,
    isPublic: true,
    maxNumAttempts: type === 'AI_UPLOAD' ? 1 : undefined,
    options: type === 'MULTIPLE_CHOICE' ? ['', ''] : [],
    parts:
      type === 'MULTI_PART'
        ? [createEmptyQuestionPart('EXACT_ANSWER')]
        : undefined,
    tags: [],
    type,
    useCustomNumAttempts: type === 'AI_UPLOAD' ? true : false,
    useRandomVariables: false,
    value: type === 'AI_UPLOAD' ? getRubricTotalScore(gradingRubric) : 1,
    variableBasedAnswer: type === 'AI_UPLOAD' ? undefined : '',
    variables: [],
  };
}

function stripHtml(value: string | undefined) {
  if (!value) {
    return '';
  }

  return value.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function extractQuestionPreviewText(
  question: Pick<EditableQuestion, 'contentHtml' | 'type'>,
  maxLength = 100,
) {
  const baseText = stripHtml(question.contentHtml) || `${question.type} question`;
  return baseText.length > maxLength
    ? `${baseText.slice(0, maxLength).trimEnd()}…`
    : baseText;
}

function collectVariableReferences(source: string | undefined, names: Set<string>) {
  if (!source) {
    return;
  }

  const pattern = /##\s*([A-Za-z0-9_]+)\s*##/g;
  let match = pattern.exec(source);

  while (match) {
    const name = match[1]?.trim();

    if (name) {
      names.add(name);
    }

    match = pattern.exec(source);
  }
}

export function detectQuestionVariableNames(
  question: Pick<EditableQuestion, 'contentHtml' | 'options' | 'parts'>,
) {
  const names = new Set<string>();

  collectVariableReferences(question.contentHtml, names);
  question.options.forEach((option) => {
    collectVariableReferences(option, names);
  });
  question.parts?.forEach((part) => {
    collectVariableReferences(part.contentHtml, names);
    part.options.forEach((option) => {
      collectVariableReferences(option, names);
    });
  });

  return Array.from(names).sort((left, right) => left.localeCompare(right));
}

/**
 * Return an editable variable definition (NUMERICAL, empty Min/Max) for every
 * `##name##` referenced in the question that does not already have a row, so the
 * instructor can configure its range. Existing variables — including any
 * user-entered Min/Max and manually-added variables — are matched by name and
 * left untouched. Used to keep the variables panel in sync with detected
 * references.
 */
export function getMissingDetectedVariables(
  question: Pick<
    EditableQuestion,
    'contentHtml' | 'id' | 'options' | 'parts' | 'variables'
  >,
): EditableVariable[] {
  const existingNames = new Set(question.variables.map((variable) => variable.name));

  return detectQuestionVariableNames(question)
    .filter((name) => !existingNames.has(name))
    .map((name, index) => ({
      id: `${question.id}-variable-${question.variables.length + index + 1}`,
      name,
      type: 'NUMERICAL' as const,
    }));
}

function getQuestionListKey(kind: QuestionSectionKind) {
  return kind === 'homework' ? 'questions' : 'preLectureQuestions';
}

const QUESTION_SETTINGS_KEYS: Record<QuestionSectionKind, Array<keyof RawComponent>> = {
  homework: [
    'question_settings',
    'questionSettings',
    'course_question_settings',
    'courseQuestionSettings',
  ],
  preLecture: ['pre_lecture_question_settings', 'preLectureQuestionSettings'],
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function normalizeSettingNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : undefined;
}

function isUserDefinedQuestionSettings(settings: RawQuestionSettings) {
  if (settings.type === 4) {
    return true;
  }

  return typeof settings.type === 'string'
    ? settings.type.trim().toUpperCase() === 'USER_DEFINED' ||
      settings.type.trim() === '4'
    : false;
}

function getAssignedQuestionIndices(settings: RawQuestionSettings) {
  const rawNumbers = settings.user_defined_numbers ?? settings.userDefinedNumbers;

  if (!Array.isArray(rawNumbers)) {
    return [];
  }

  return rawNumbers
    .map((entry) => normalizeSettingNumber(entry))
    .filter((entry): entry is number => entry !== undefined)
    .map((entry) => Math.trunc(entry));
}

function updateQuestionSettingIndices(
  baseComponent: RawComponent,
  kind: QuestionSectionKind,
  updater: (indices: number[]) => number[],
): RawComponent {
  let didUpdate = false;
  const nextComponent = { ...baseComponent };

  QUESTION_SETTINGS_KEYS[kind].forEach((key) => {
    const settings = nextComponent[key];

    if (!isRecord(settings)) {
      return;
    }

    const questionSettings = settings as RawQuestionSettings;

    if (!isUserDefinedQuestionSettings(questionSettings)) {
      return;
    }

    const nextIndices = updater(getAssignedQuestionIndices(questionSettings));
    nextComponent[key] = {
      ...settings,
      ...(Object.prototype.hasOwnProperty.call(settings, 'userDefinedNumbers')
        ? { userDefinedNumbers: nextIndices }
        : {}),
      user_defined_numbers: nextIndices,
    } as never;
    didUpdate = true;
  });

  return didUpdate ? nextComponent : baseComponent;
}

function getQuestionPartValidationError(part: EditableQuestionPart) {
  if (part.type === 'MULTIPLE_CHOICE') {
    if (part.options.length < 2) {
      return 'Multiple-choice parts need at least two options.';
    }

    if (part.options.some((option) => option.trim().length === 0)) {
      return 'Each option needs text before the part can be saved.';
    }

    if (
      part.correctAnswerIndex === undefined ||
      part.correctAnswerIndex < 0 ||
      part.correctAnswerIndex >= part.options.length
    ) {
      return 'Choose the correct answer for this part.';
    }
  }

  if (part.type === 'NUMERICAL_WITH_ERROR') {
    if (
      !hasVariableBasedAnswer(part.variableBasedAnswer) &&
      (part.numericalAnswer === undefined || Number.isNaN(part.numericalAnswer))
    ) {
      return 'A numerical answer is required for this part.';
    }

    if (part.numericalError === undefined || Number.isNaN(part.numericalError)) {
      return 'An error tolerance is required for this part.';
    }
  }

  if (
    part.type === 'EXACT_ANSWER' &&
    !normalizeString(part.exactAnswer) &&
    !hasVariableBasedAnswer(part.variableBasedAnswer)
  ) {
    return 'An exact answer is required for this part.';
  }

  if (part.type === 'AI_UPLOAD') {
    const rubric = normalizeGradingRubric(part.gradingRubric);

    if (rubric.rubricUnits.length === 0) {
      return 'Add at least one rubric unit before saving this AI Upload part.';
    }

    if (rubric.rubricUnits.some((unit) => unit.name.trim().length === 0)) {
      return 'Each rubric unit needs a name before the part can be saved.';
    }

    if (
      rubric.rubricUnits.some(
        (unit) => !Number.isFinite(unit.score) || unit.score <= 0,
      )
    ) {
      return 'Each rubric unit needs a positive point value.';
    }
  }

  return undefined;
}

function getQuestionValidationError(question: EditableQuestion) {
  if (question.type === 'UNSUPPORTED') {
    return undefined;
  }

  if (question.type === 'MULTIPLE_CHOICE') {
    if (question.options.length < 2) {
      return 'Multiple-choice questions need at least two options.';
    }

    if (question.options.some((option) => option.trim().length === 0)) {
      return 'Each option needs text before the question can be saved.';
    }

    if (
      question.correctAnswerIndex === undefined ||
      question.correctAnswerIndex < 0 ||
      question.correctAnswerIndex >= question.options.length
    ) {
      return 'Choose the correct answer for this question.';
    }
  }

  if (question.type === 'NUMERICAL_WITH_ERROR') {
    if (
      !hasVariableBasedAnswer(question.variableBasedAnswer) &&
      (question.numericalAnswer === undefined || Number.isNaN(question.numericalAnswer))
    ) {
      return 'A numerical answer is required for this question.';
    }

    if (question.numericalError === undefined || Number.isNaN(question.numericalError)) {
      return 'An error tolerance is required for this question.';
    }
  }

  if (
    question.type === 'EXACT_ANSWER' &&
    !normalizeString(question.exactAnswer) &&
    !hasVariableBasedAnswer(question.variableBasedAnswer)
  ) {
    return 'An exact answer is required for this question.';
  }

  if (question.type === 'MULTI_PART' && (!question.parts || question.parts.length === 0)) {
    return 'Add at least one part before saving this multi-part question.';
  }

  if (question.type === 'AI_UPLOAD') {
    const rubric = normalizeGradingRubric(question.gradingRubric);

    if (rubric.rubricUnits.length === 0) {
      return 'Add at least one rubric unit before saving this AI Upload question.';
    }

    if (rubric.rubricUnits.some((unit) => unit.name.trim().length === 0)) {
      return 'Each rubric unit needs a name before the question can be saved.';
    }

    if (
      rubric.rubricUnits.some(
        (unit) => !Number.isFinite(unit.score) || unit.score <= 0,
      )
    ) {
      return 'Each rubric unit needs a positive point value.';
    }
  }

  return undefined;
}

function validateQuestion(question: EditableQuestion): EditableQuestion {
  const validationError = getQuestionValidationError(question);
  const parts = question.parts?.map((part) => ({
    ...part,
    validationError: getQuestionPartValidationError(part),
  }));

  return {
    ...question,
    parts,
    validationError:
      validationError ??
      (parts?.some((part) => part.validationError)
        ? 'Fix the invalid part fields before saving.'
        : undefined),
  };
}

export function normalizeYouTubeVideoId(value: string) {
  const trimmed = value.trim();

  if (!trimmed) {
    return null;
  }

  try {
    const parsed = new URL(trimmed);
    const host = parsed.hostname.replace(/^www\./, '').toLowerCase();

    if (host === 'youtu.be') {
      return parsed.pathname.split('/').filter(Boolean)[0]?.trim() ?? null;
    }

    if (host === 'youtube.com' || host === 'm.youtube.com') {
      const fromSearch = parsed.searchParams.get('v')?.trim();

      if (fromSearch) {
        return fromSearch;
      }

      const segments = parsed.pathname.split('/').filter(Boolean);

      if (segments[0] === 'embed' && segments[1]) {
        return segments[1].trim();
      }
    }
  } catch {
    // Fall through to a bare ID check.
  }

  if (/^[A-Za-z0-9_-]{6,}$/.test(trimmed)) {
    return trimmed;
  }

  return null;
}

export function useContentEditor(initialState: EditorState | null) {
  const [editorState, setEditorState] = useState<EditorState | null>(
    withFallbackVideoSectionOwner(initialState),
  );

  useEffect(() => {
    if (!initialState) {
      return;
    }

    setEditorState((currentState) =>
      currentState ? currentState : withFallbackVideoSectionOwner(initialState),
    );
  }, [initialState]);

  const replaceState = useCallback((nextState: EditorState) => {
    setEditorState(withFallbackVideoSectionOwner(nextState));
  }, []);

  const setSaving = useCallback((isSaving: boolean) => {
    setEditorState((currentState) =>
      currentState
        ? {
            ...currentState,
            isSaving,
          }
        : currentState,
    );
  }, []);

  const updateUnit = useCallback((
    unitId: string,
    updates: Partial<EditableContentUnit>,
  ) => {
    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      return {
        ...currentState,
        contentUnits: currentState.contentUnits.map((unit) =>
          unit.id === unitId
            ? (() => {
                const nextUnit: EditableContentUnit = {
                  ...unit,
                  ...updates,
                  caption:
                    updates.caption !== undefined
                      ? updates.caption
                      : unit.caption,
                  heading:
                    updates.heading !== undefined
                      ? updates.heading
                      : unit.heading,
                };

                if (
                  unit.editorType === 'IMAGE' &&
                  ('imageUrl' in updates || 'dynamicImageUrl' in updates)
                ) {
                  nextUnit.validationError = undefined;
                }

                return nextUnit;
              })()
            : unit,
        ),
        isDirty: true,
      };
    });
  }, []);

  const insertUnitAt = useCallback((insertIndex: number, editorType: AddableContentType) => {
    if (!editorState) {
      return null;
    }

    const preparedState = ensureEditableOwnerSection(editorState).state;
    const ownership = getOwnershipForInsertion(
      preparedState.baseComponent,
      preparedState.contentUnits,
      insertIndex,
    );

    if (!ownership) {
      return null;
    }

    const newUnit = createEmptyUnit(editorType, ownership);

    setEditorState(() => {
      const nextUnits = [...preparedState.contentUnits];
      nextUnits.splice(insertIndex, 0, newUnit);

      return {
        ...preparedState,
        contentUnits: nextUnits,
        isDirty: true,
      };
    });

    return newUnit.id;
  }, [editorState]);

  const deleteUnit = useCallback((unitId: string) => {
    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      return {
        ...currentState,
        contentUnits: currentState.contentUnits.filter((unit) => unit.id !== unitId),
        isDirty: true,
      };
    });
  }, []);

  const moveUnit = useCallback((fromIndex: number, toIndex: number) => {
    setEditorState((currentState) => {
      if (!currentState || fromIndex === toIndex) {
        return currentState;
      }

      return {
        ...currentState,
        contentUnits: moveArrayItem(currentState.contentUnits, fromIndex, toIndex),
        isDirty: true,
      };
    });
  }, []);

  const replaceContentUnits = useCallback((
    contentUnits: EditableContentUnit[],
    options: { markDirty?: boolean } = {},
  ) => {
    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      return {
        ...currentState,
        contentUnits,
        isDirty: options.markDirty ?? true,
      };
    });
  }, []);

  const resetToBaseComponent = useCallback(() => {
    setEditorState((currentState) =>
      currentState ? createEditorState(currentState.baseComponent) : currentState,
    );
  }, []);

  const updateComponentTitle = useCallback((title: string) => {
    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      return {
        ...currentState,
        baseComponent: {
          ...currentState.baseComponent,
          title,
        },
        isDirty: true,
      };
    });
  }, []);

  const addVideo = useCallback((value: string): AddVideoResult => {
    const normalizedVideoId = normalizeYouTubeVideoId(value);

    if (!normalizedVideoId) {
      return {
        error: 'Enter a YouTube video ID or a supported YouTube URL.',
        success: false,
      };
    }

    let didAdd = false;

    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      didAdd = true;

      const preparedState = ensureVideoOwnerSection(currentState);

      return {
        ...preparedState,
        isDirty: true,
        videoIds: [...preparedState.videoIds, normalizedVideoId],
      };
    });

    return { success: didAdd };
  }, []);

  const removeVideo = useCallback((index: number) => {
    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      return {
        ...currentState,
        isDirty: true,
        videoIds: currentState.videoIds.filter((_, currentIndex) => currentIndex !== index),
      };
    });
  }, []);

  const reorderVideos = useCallback((fromIndex: number, toIndex: number) => {
    setEditorState((currentState) => {
      if (
        !currentState ||
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= currentState.videoIds.length ||
        toIndex >= currentState.videoIds.length
      ) {
        return currentState;
      }

      return {
        ...currentState,
        isDirty: true,
        videoIds: moveArrayItem(currentState.videoIds, fromIndex, toIndex),
      };
    });
  }, []);

  const replaceVideoIds = useCallback((videoIds: string[]) => {
    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      const preparedState = videoIds.length > 0
        ? ensureVideoOwnerSection(currentState)
        : currentState;

      return {
        ...preparedState,
        isDirty: true,
        videoIds: [...videoIds],
      };
    });
  }, []);

  const updateQuestionCollection = useCallback((
    kind: QuestionSectionKind,
    updater: (questions: EditableQuestion[]) => EditableQuestion[],
    options?: { markDirty?: boolean },
  ) => {
    const key = getQuestionListKey(kind);

    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      const preparedState = ensureQuestionOwnerSection(currentState, kind);

      return {
        ...preparedState,
        [key]: updater(preparedState[key]),
        isDirty: preparedState.isDirty || (options?.markDirty ?? true),
      };
    });
  }, []);

  const addQuestion = useCallback((
    kind: QuestionSectionKind,
    questionOrType: AddableQuestionType | EditableQuestion,
    insertIndex?: number,
    options?: { markDirty?: boolean },
  ) => {
    updateQuestionCollection(kind, (questions) => {
      const nextQuestions = [...questions];
      const nextQuestion =
        typeof questionOrType === 'string'
          ? createEmptyQuestion(questionOrType)
          : {
              ...questionOrType,
              isNew: questionOrType.isNew ?? true,
            };
      const safeIndex =
        insertIndex === undefined || insertIndex < 0 || insertIndex > nextQuestions.length
          ? nextQuestions.length
          : insertIndex;

      nextQuestions.splice(safeIndex, 0, nextQuestion);
      return nextQuestions;
    }, options);
  }, [updateQuestionCollection]);

  const removeQuestion = useCallback((
    kind: QuestionSectionKind,
    index: number,
    options?: { markDirty?: boolean },
  ) => {
    const key = getQuestionListKey(kind);

    setEditorState((currentState) => {
      if (!currentState || index < 0 || index >= currentState[key].length) {
        return currentState;
      }

      return {
        ...currentState,
        [key]: currentState[key].filter(
          (_question, currentIndex) => currentIndex !== index,
        ),
        baseComponent: updateQuestionSettingIndices(
          currentState.baseComponent,
          kind,
          (indices) => remapAssignedIndicesAfterDelete(indices, index),
        ),
        isDirty: currentState.isDirty || (options?.markDirty ?? true),
      };
    });
  }, []);

  const reorderQuestions = useCallback((
    kind: QuestionSectionKind,
    fromIndex: number,
    toIndex: number,
  ) => {
    const key = getQuestionListKey(kind);

    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      const questions = currentState[key];

      if (
        fromIndex === toIndex ||
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= questions.length ||
        toIndex >= questions.length
      ) {
        return currentState;
      }

      return {
        ...currentState,
        [key]: moveArrayItem(questions, fromIndex, toIndex),
        isDirty: true,
      };
    });
  }, []);

  const reorderQuestionAssignment = useCallback((
    kind: QuestionSectionKind,
    fromPosition: number,
    toPosition: number,
  ) => {
    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      return {
        ...currentState,
        baseComponent: updateQuestionSettingIndices(
          currentState.baseComponent,
          kind,
          (indices) => reorderAssignment(indices, fromPosition, toPosition),
        ),
        isDirty: true,
      };
    });
  }, []);

  const updateQuestion = useCallback((
    kind: QuestionSectionKind,
    index: number,
    updates: Partial<EditableQuestion>,
  ) => {
    updateQuestionCollection(kind, (questions) =>
      questions.map((question, currentIndex) =>
        currentIndex === index
          ? {
              ...question,
              ...updates,
              validationError: undefined,
            }
          : question,
      ),
    );
  }, [updateQuestionCollection]);

  const addQuestionPart = useCallback((
    kind: QuestionSectionKind,
    questionIndex: number,
    partType: AddableQuestionPartType,
  ) => {
    updateQuestionCollection(kind, (questions) =>
      questions.map((question, currentIndex) =>
        currentIndex === questionIndex
          ? {
              ...question,
              parts: [...(question.parts ?? []), createEmptyQuestionPart(partType)],
              validationError: undefined,
            }
          : question,
      ),
    );
  }, [updateQuestionCollection]);

  const removeQuestionPart = useCallback((
    kind: QuestionSectionKind,
    questionIndex: number,
    partIndex: number,
  ) => {
    updateQuestionCollection(kind, (questions) =>
      questions.map((question, currentIndex) =>
        currentIndex === questionIndex
          ? {
              ...question,
              parts: (question.parts ?? []).filter(
                (_, currentPartIndex) => currentPartIndex !== partIndex,
              ),
              validationError: undefined,
            }
          : question,
      ),
    );
  }, [updateQuestionCollection]);

  const reorderQuestionParts = useCallback((
    kind: QuestionSectionKind,
    questionIndex: number,
    fromIndex: number,
    toIndex: number,
  ) => {
    updateQuestionCollection(kind, (questions) =>
      questions.map((question, currentIndex) =>
        currentIndex === questionIndex
          ? {
              ...question,
              parts: moveArrayItem(question.parts ?? [], fromIndex, toIndex),
              validationError: undefined,
            }
          : question,
      ),
    );
  }, [updateQuestionCollection]);

  const updateQuestionPart = useCallback((
    kind: QuestionSectionKind,
    questionIndex: number,
    partIndex: number,
    updates: Partial<EditableQuestionPart>,
  ) => {
    updateQuestionCollection(kind, (questions) =>
      questions.map((question, currentIndex) =>
        currentIndex === questionIndex
          ? {
              ...question,
              parts: (question.parts ?? []).map((part, currentPartIndex) =>
                currentPartIndex === partIndex
                  ? {
                      ...part,
                      ...updates,
                      validationError: undefined,
                    }
                  : part,
              ),
              validationError: undefined,
            }
          : question,
      ),
    );
  }, [updateQuestionCollection]);

  const validateBeforeSave = useCallback(() => {
    if (!editorState) {
      return false;
    }

    const nextUnits = editorState.contentUnits.map((unit) => {
      const validationError = getUnitValidationError(unit);

      return unit.validationError === validationError
        ? unit
        : {
            ...unit,
            validationError,
          };
    });
    const nextQuestions = editorState.questions.map((question) =>
      isQuestionUnchanged(question) ? clearQuestionValidation(question) : validateQuestion(question),
    );
    const nextPreLectureQuestions = editorState.preLectureQuestions.map((question) =>
      isQuestionUnchanged(question) ? clearQuestionValidation(question) : validateQuestion(question),
    );

    const isValid =
      nextUnits.every((unit) => !unit.validationError) &&
      nextQuestions.every(
        (question) =>
          !question.validationError &&
          !(question.parts ?? []).some((part) => part.validationError),
      ) &&
      nextPreLectureQuestions.every(
        (question) =>
          !question.validationError &&
          !(question.parts ?? []).some((part) => part.validationError),
      );

    setEditorState((currentState) => {
      if (!currentState) {
        return currentState;
      }

      return {
        ...currentState,
        contentUnits: nextUnits,
        preLectureQuestions: nextPreLectureQuestions,
        questions: nextQuestions,
      };
    });

    if (!isValid) {
      const failingUnits = nextUnits.filter((unit) => unit.validationError);
      const failingQuestions = nextQuestions.filter(
        (question) =>
          question.validationError ||
          (question.parts ?? []).some((part) => part.validationError),
      );
      const failingPreLecture = nextPreLectureQuestions.filter(
        (question) =>
          question.validationError ||
          (question.parts ?? []).some((part) => part.validationError),
      );

      console.warn(
        '[Validate] Failing units:',
        failingUnits.map((unit) => ({
          error: unit.validationError,
          id: unit.id,
          type: unit.editorType,
        })),
      );
      console.warn(
        '[Validate] Failing questions:',
        failingQuestions.map((question) => ({
          error: question.validationError,
          id: question.id,
          parts: question.parts
            ?.filter((part) => part.validationError)
            .map((part) => ({
              error: part.validationError,
              id: part.id,
              type: part.type,
            })),
          type: question.type,
        })),
      );
      console.warn(
        '[Validate] Failing pre-lecture:',
        failingPreLecture.map((question) => ({
          error: question.validationError,
          id: question.id,
          parts: question.parts
            ?.filter((part) => part.validationError)
            .map((part) => ({
              error: part.validationError,
              id: part.id,
              type: part.type,
            })),
          type: question.type,
        })),
      );
    }

    return isValid;
  }, [editorState]);

  return useMemo(() => ({
    addQuestion,
    addQuestionPart,
    addVideo,
    contentUnits: editorState?.contentUnits ?? [],
    deleteUnit,
    editorState,
    insertUnitAt,
    isDirty: editorState?.isDirty ?? false,
    isSaving: editorState?.isSaving ?? false,
    moveUnit,
    preLectureQuestionSectionOwnerSectionId:
      editorState?.preLectureQuestionSectionOwnerSectionId,
    preLectureQuestions: editorState?.preLectureQuestions ?? [],
    questionSectionOwnerSectionId: editorState?.questionSectionOwnerSectionId,
    questions: editorState?.questions ?? [],
    removeQuestion,
    removeQuestionPart,
    removeVideo,
    replaceContentUnits,
    replaceState,
    reorderQuestionAssignment,
    reorderQuestionParts,
    reorderQuestions,
    reorderVideos,
    replaceVideoIds,
    resetToBaseComponent,
    setSaving,
    updateComponentTitle,
    updateQuestion,
    updateQuestionPart,
    updateUnit,
    validateBeforeSave,
    videoIds: editorState?.videoIds ?? [],
    videoSectionOwnerSectionId: editorState?.videoSectionOwnerSectionId,
  }), [
    addQuestion,
    addQuestionPart,
    addVideo,
    deleteUnit,
    editorState,
    insertUnitAt,
    moveUnit,
    removeQuestion,
    removeQuestionPart,
    removeVideo,
    replaceContentUnits,
    replaceState,
    reorderQuestionAssignment,
    reorderQuestionParts,
    reorderQuestions,
    reorderVideos,
    replaceVideoIds,
    resetToBaseComponent,
    setSaving,
    updateComponentTitle,
    updateQuestion,
    updateQuestionPart,
    updateUnit,
    validateBeforeSave,
  ]);
}
